import { useCallback, useEffect, useState } from "react";

// Per-device price-visibility preference for logged-in traders.
//
// A trader sometimes hands their phone or tablet to a retail customer to show
// designs. In that moment they do NOT want their wholesale price visible to
// the retail buyer (the trader's own margin is private). This toggle lets them
// hide every price on the catalog / series / card / WhatsApp enquiry text
// with one tap.
//
// State is persisted in localStorage per-device (multi-device traders won't
// accidentally hide on phone B when they toggled off on phone A).
// A `storage` listener + custom event keeps every mounted component in sync.

const STORAGE_KEY = "pk_prices_visible";
const EVENT_NAME = "pk:prices-visible-changed";

/** Synchronous read — useful for non-React code (e.g. WhatsApp text builders). */
export function arePricesVisible() {
  try {
    return localStorage.getItem(STORAGE_KEY) !== "false";
  } catch {
    return true; // default visible (private mode / SSR safe)
  }
}

/** Programmatic setter — broadcasts to all mounted hook instances. */
export function setPricesVisible(next) {
  try { localStorage.setItem(STORAGE_KEY, next ? "true" : "false"); } catch { /* private mode */ }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { visible: !!next } }));
  }
}

/** React hook — returns `{ visible, toggle, setVisible }`. */
export function usePriceVisibility() {
  const [visible, setVisibleState] = useState(() => arePricesVisible());

  useEffect(() => {
    const onCustom = (e) => setVisibleState(!!(e.detail && e.detail.visible));
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      setVisibleState(e.newValue !== "false");
    };
    window.addEventListener(EVENT_NAME, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setVisible = useCallback((next) => setPricesVisible(next), []);
  const toggle = useCallback(() => setPricesVisible(!arePricesVisible()), []);

  return { visible, toggle, setVisible };
}

/** Format an amount honoring the trader's visibility toggle.
 *  When hidden we render a long em-dash (—) which is short, neutral, and
 *  the same width across both the catalog tile and lightbox bar so the
 *  layout doesn't jump when toggled on/off. */
export function formatPriceForTrader(amount, visible) {
  if (!visible) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
